/**
 * MiniMapCell.jsx
 * Componente de mini-mapa estatico para linhas de relatorios
 *
 * Design System Premium Fleet v2.0
 *
 * Uso:
 *   <MiniMapCell
 *     latitude={-23.5505}
 *     longitude={-46.6333}
 *     markerIcon="alert"
 *     onClick={() => setExpandedMap({ lat, lng })}
 *   />
 */

import {
 useState, useMemo, useCallback, useRef, useEffect 
} from 'react';
import {
  Box,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { usePremiumTokens } from '../../theme/usePremiumTokens';

// ============================================================================
// CONSTANTES
// ============================================================================

const SIZES = {
  xs: { width: 60, height: 45 },
  sm: { width: 80, height: 60 },
  md: { width: 120, height: 90 },
  lg: { width: 160, height: 120 },
  // Tamanho especial para print - maior para melhor visualização
  print: { width: 100, height: 75 },
};

const MARKER_COLORS = {
  default: '#00d4aa',
  pin: '#00d4aa',
  alert: '#f85149',
  alarm: '#f85149',
  enter: '#22c55e',
  exit: '#f97316',
  car: '#58a6ff',
  stop: '#d29922',
  secondary: '#8b5cf6',
};

// Cache global de tiles pre-carregados
const tileCache = new Map();

// ============================================================================
// FUNCOES UTILITARIAS PARA TILES OSM
// ============================================================================

/**
 * Converte latitude/longitude para coordenadas de tile
 */
const latLngToTile = (lat, lng, zoom) => {
  const n = Math.pow(2, zoom);
  const x = Math.floor((lng + 180) / 360 * n);
  const latRad = lat * Math.PI / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return { x, y, z: zoom };
};

/**
 * Calcula offset do pixel dentro do tile
 */
const getPixelOffset = (lat, lng, zoom, tileSize = 256) => {
  const n = Math.pow(2, zoom);
  const x = ((lng + 180) / 360 * n) % 1 * tileSize;
  const latRad = lat * Math.PI / 180;
  const y = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n) % 1 * tileSize;
  return { x, y };
};

/**
 * Gera URL do tile OSM
 * Usa CDN confiavel do OpenStreetMap
 */
const getOSMTileUrl = (x, y, z) => {
  // Usa subdomains a, b, c para distribuir carga
  const subdomain = ['a', 'b', 'c'][Math.abs(x + y) % 3];
  return `https://${subdomain}.tile.openstreetmap.org/${z}/${x}/${y}.png`;
};

/**
 * Pre-carrega um tile em background e adiciona ao cache
 */
const preloadTile = (url) => {
  if (tileCache.has(url)) {
    return tileCache.get(url);
  }

  const promise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve({ loaded: true, url });
    img.onerror = () => resolve({ loaded: false, url });
    img.src = url;
  });

  tileCache.set(url, promise);
  return promise;
};

// ============================================================================
// COMPONENTE: MarkerOverlay (Flag style - works in print)
// A ponta do mastro fica exatamente na coordenada (centro do mini-mapa)
// ============================================================================

const MarkerOverlay = ({ color, size, forPrint = false }) => {
  // Tamanhos da bandeirinha
  const flagHeight = forPrint ? 14 : (size === 'xs' ? 10 : size === 'sm' ? 12 : size === 'md' ? 14 : 16);
  const flagWidth = Math.round(flagHeight * 0.75);
  const poleHeight = forPrint ? 18 : (size === 'xs' ? 14 : size === 'sm' ? 16 : size === 'md' ? 18 : 20);
  const poleWidth = forPrint ? 2 : 2;

  // Altura total = mastro + bandeira
  const totalHeight = poleHeight + flagHeight;

  return (
    <Box
      className="mini-map-marker"
      sx={{
        position: 'absolute',
        // Posiciona no centro do mini-mapa
        top: '50%',
        left: '50%',
        // Desloca para que a PONTA do mastro fique no centro
        // marginLeft: -1px para centralizar o mastro de 2px
        transform: `translate(-1px, -${totalHeight}px)`,
        zIndex: 10,
        pointerEvents: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      {/* Bandeira (triangulo) - no topo */}
      <Box
        className="mini-map-flag"
        sx={{
          width: 0,
          height: 0,
          borderTop: `${flagHeight / 2}px solid transparent`,
          borderBottom: `${flagHeight / 2}px solid transparent`,
          borderLeft: `${flagWidth}px solid ${color}`,
          marginLeft: `${poleWidth}px`,
          // Print styles - CRITICAL
          WebkitPrintColorAdjust: 'exact !important',
          printColorAdjust: 'exact !important',
          colorAdjust: 'exact !important',
        }}
      />
      {/* Mastro (pole) - abaixo da bandeira */}
      <Box
        className="mini-map-pole"
        sx={{
          width: `${poleWidth}px`,
          height: `${poleHeight}px`,
          backgroundColor: '#444',
          // Print styles - CRITICAL
          WebkitPrintColorAdjust: 'exact !important',
          printColorAdjust: 'exact !important',
          colorAdjust: 'exact !important',
        }}
      />
    </Box>
  );
};

// ============================================================================
// COMPONENTE: FallbackDisplay
// ============================================================================

const FallbackDisplay = ({ latitude, longitude, color, onClick, dimensions, tokens, forPrint }) => (
  <Box
    sx={{
      width: dimensions.width,
      height: dimensions.height,
      backgroundColor: forPrint ? '#f5f5f5' : (tokens?.surface?.sunken || '#1c2128'),
      borderRadius: '4px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${forPrint ? '#ddd' : (tokens?.border?.subtle || 'rgba(255,255,255,0.1)')}`,
      cursor: onClick ? 'pointer' : 'default',
    }}
    onClick={onClick}
  >
    <LocationOnIcon
      sx={{
        fontSize: 20,
        color,
        mb: 0.25,
      }}
    />
    <Typography
      variant="caption"
      sx={{
        color: forPrint ? '#333' : (tokens?.text?.secondary || '#8b949e'),
        fontSize: '8px',
        lineHeight: 1.2,
        textAlign: 'center',
      }}
    >
      {latitude?.toFixed(4)}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: forPrint ? '#666' : (tokens?.text?.muted || '#6e7681'),
        fontSize: '7px',
        lineHeight: 1.2,
      }}
    >
      {longitude?.toFixed(4)}
    </Typography>
  </Box>
);

// ============================================================================
// COMPONENTE PRINCIPAL: MiniMapCell
// ============================================================================

const MiniMapCell = ({
  // Posicao principal
  latitude,
  longitude,

  // Posicao secundaria (para correlacao)
  secondaryLatitude,
  secondaryLongitude,

  // Customizacao
  size = 'sm',
  zoom = 16, // Aumentado para melhor detalhe
  markerIcon = 'pin',
  markerColor,

  // Velocidade para determinar cor da bandeira (verde=movimento, vermelho=parado)
  speed,

  // Interacao
  onClick,
  showExpandButton = true,

  // Print mode
  forPrint = false,

  // Tooltip
  tooltipTitle,

  // Estilo extra
  sx,
}) => {
  const tokens = usePremiumTokens();
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(forPrint); // Start visible for print
  const containerRef = useRef(null);

  // Use print size when forPrint is true, otherwise use specified size
  const dimensions = forPrint ? SIZES.print : (SIZES[size] || SIZES.sm);

  // Determina cor: se speed foi passado, usa verde/vermelho baseado em movimento
  // Caso contrario, usa markerColor ou MARKER_COLORS baseado em markerIcon
  const color = useMemo(() => {
    if (speed !== undefined && speed !== null) {
      // Verde para movimento (speed > 0), vermelho para parado
      return speed > 0 ? '#22c55e' : '#ef4444';
    }
    return markerColor || MARKER_COLORS[markerIcon] || MARKER_COLORS.default;
  }, [speed, markerColor, markerIcon]);

  // SEMPRE calcula tileData - permite pre-loading independente de visibilidade
  const tileData = useMemo(() => {
    if (!latitude || !longitude) return null;

    const tile = latLngToTile(latitude, longitude, zoom);
    const offset = getPixelOffset(latitude, longitude, zoom);
    const url = getOSMTileUrl(tile.x, tile.y, tile.z);

    return { tile, offset, url };
  }, [latitude, longitude, zoom]);

  // Pre-load tile em background assim que o componente monta
  // Isso coloca o tile no cache do browser antes de precisar renderizar
  useEffect(() => {
    if (tileData?.url) {
      preloadTile(tileData.url);
    }
  }, [tileData?.url]);

  // Lazy loading com IntersectionObserver - rootMargin aumentado para 1000px
  useEffect(() => {
    if (forPrint) {
      setIsVisible(true);
      return;
    }

    if (!containerRef.current) {
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        // Aumentado para 1000px - carrega tiles muito antes de ficarem visiveis
        rootMargin: '1000px',
        threshold: 0,
      },
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [forPrint]);

  // Handler de click
  const handleClick = useCallback((e) => {
    if (onClick) {
      e.stopPropagation();
      onClick();
    }
  }, [onClick]);

  // Timeout para imagens que demoram muito (apenas para tela, nao print)
  useEffect(() => {
    if (!tileData || loaded || error || forPrint) return;

    const timeout = setTimeout(() => {
      if (!loaded) {
        setError(true);
      }
    }, 10000); // 10 segundos timeout

    return () => clearTimeout(timeout);
  }, [tileData, loaded, error, forPrint]);

  // Sem coordenadas
  if (!latitude || !longitude) {
    return (
      <Box
        ref={containerRef}
        sx={{
          width: dimensions.width,
          height: dimensions.height,
          backgroundColor: forPrint ? '#f5f5f5' : (tokens?.surface?.sunken || '#010409'),
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid ${forPrint ? '#ddd' : (tokens?.border?.subtle || 'rgba(255,255,255,0.06)')}`,
          ...sx,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: forPrint ? '#999' : (tokens?.text?.muted || '#6e7681'),
            fontSize: '10px',
          }}
        >
          Sem loc.
        </Typography>
      </Box>
    );
  }

  // Erro ou timeout - mostra fallback com coordenadas (apenas para tela)
  if (error && !forPrint) {
    return (
      <Box ref={containerRef} sx={sx}>
        <FallbackDisplay
          latitude={latitude}
          longitude={longitude}
          color={color}
          onClick={onClick ? handleClick : undefined}
          dimensions={dimensions}
          tokens={tokens}
          forPrint={forPrint}
        />
      </Box>
    );
  }

  // Calcula posição do tile para centralizar a coordenada
  const tileLeft = tileData ? (dimensions.width / 2 - tileData.offset.x) : 0;
  const tileTop = tileData ? (dimensions.height / 2 - tileData.offset.y) : 0;

  // Para print: sempre mostrar tile e marker, mesmo se nao "loaded"
  // O browser vai esperar a imagem carregar antes de imprimir
  const showTile = forPrint || isVisible;
  const showMarker = forPrint || loaded;

  const content = (
    <Box
      ref={containerRef}
      className="mini-map-cell"
      sx={{
        position: 'relative',
        width: dimensions.width,
        height: dimensions.height,
        borderRadius: '4px',
        overflow: 'hidden',
        border: `1px solid ${forPrint ? '#ccc' : (tokens?.border?.subtle || 'rgba(255,255,255,0.06)')}`,
        cursor: onClick && !forPrint ? 'pointer' : 'default',
        transition: forPrint ? 'none' : 'transform 0.2s, box-shadow 0.2s',
        backgroundColor: forPrint ? '#e8e8e8' : (tokens?.surface?.sunken || '#010409'),
        '&:hover': (!forPrint && onClick) ? {
          transform: 'scale(1.03)',
          boxShadow: `0 4px 12px ${tokens?.accent?.glow || 'rgba(0,212,170,0.2)'}`,
          borderColor: tokens?.accent?.primary || '#00d4aa',
        } : {},
        // Print-specific styles
        '@media print': {
          border: '1px solid #ccc',
          backgroundColor: '#e8e8e8',
        },
        ...sx,
      }}
      onClick={!forPrint ? handleClick : undefined}
    >
      {/* Loading skeleton - only for screen when not loaded */}
      {!loaded && !forPrint && (
        <Skeleton
          variant="rectangular"
          width={dimensions.width}
          height={dimensions.height}
          animation="wave"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundColor: tokens?.surface?.hover || '#1c2128',
          }}
        />
      )}

      {/* Tile do mapa - usa IMG real para garantir que imprima */}
      {tileData && showTile && (
        <Box
          className="mini-map-tile"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          <img
            src={tileData.url}
            alt="Mini mapa"
            style={{
              position: 'absolute',
              left: tileLeft,
              top: tileTop,
              width: 256,
              height: 256,
              // Para print: sempre opacity 1 (browser espera carregar)
              // Para tela: fade in quando carrega
              opacity: forPrint ? 1 : (loaded ? 1 : 0),
              transition: forPrint ? 'none' : 'opacity 0.3s',
              imageRendering: 'auto',
              // Prevent image from being dragged
              pointerEvents: 'none',
              userSelect: 'none',
            }}
            onLoad={() => setLoaded(true)}
            onError={() => !forPrint && setError(true)}
          />
        </Box>
      )}

      {/* Marker overlay - para print, sempre mostra */}
      {showMarker && (
        <MarkerOverlay
          color={color}
          size={size}
          forPrint={forPrint}
        />
      )}

      {/* Marcador secundario (correlacao) */}
      {showMarker && secondaryLatitude && secondaryLongitude && (
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '60%',
            transform: 'translate(-50%, -100%)',
            zIndex: 3,
            filter: forPrint ? 'none' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))',
            pointerEvents: 'none',
          }}
        >
          <LocationOnIcon
            sx={{
              fontSize: forPrint ? 20 : 16,
              color: MARKER_COLORS.secondary,
            }}
          />
        </Box>
      )}

      {/* Botao de expandir - only for screen */}
      {showExpandButton && !forPrint && loaded && onClick && (
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 2,
            right: 2,
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: 'white',
            padding: '2px',
            zIndex: 4,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.8)',
            },
            '@media print': {
              display: 'none',
            },
          }}
          onClick={handleClick}
        >
          <OpenInFullIcon sx={{ fontSize: size === 'xs' ? 10 : 12 }} />
        </IconButton>
      )}
    </Box>
  );

  // Wrap com tooltip se fornecido (only for screen)
  if (tooltipTitle && !forPrint) {
    return (
      <Tooltip title={tooltipTitle} arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default MiniMapCell;
